/**
 * The one HTTP wrapper. Every backend call goes through this — no component,
 * hook, or feature calls `fetch()` directly.
 *
 * All URLs are relative paths (e.g. `/api/v1/threads`). The backend has no
 * CORS middleware, so absolute backend URLs will be blocked by the browser.
 *
 * On 401: if a refresh token is stored, we rotate it once and replay the
 * original request. Concurrent 401s share a single refresh call — without
 * single-flight, five racing 401s would each try their own refresh, four
 * would trip the backend's reuse-detection branch, and the whole family
 * would be revoked for a signed-in reviewer with correct credentials.
 */

import {
  clearAuthSession,
  readAuthSession,
  readAuthToken,
  updateAccessToken,
} from "./auth-token"
import { ApiError, AbortedError, type ApiErrorBody } from "./errors"

/**
 * Parse `Retry-After` — RFC 9110 allows either delta-seconds or an HTTP date.
 * Backend sends seconds; we accept both. Returns null when unparseable so
 * callers fall back to a generic "try again later" message.
 */
function parseRetryAfter(header: string | null): number | null {
  if (!header) return null
  const asNumber = Number(header)
  if (Number.isFinite(asNumber) && asNumber >= 0) return Math.round(asNumber)
  const asDate = Date.parse(header)
  if (Number.isFinite(asDate)) {
    const seconds = Math.round((asDate - Date.now()) / 1000)
    return seconds > 0 ? seconds : 0
  }
  return null
}

export interface RequestOptions {
  params?: Record<string, string | number | boolean | null | undefined | string[]>
  headers?: Record<string, string>
  signal?: AbortSignal
}

interface BodyOptions<TBody> extends RequestOptions {
  body?: TBody
}

function buildUrl(path: string, params?: RequestOptions["params"]): string {
  if (!params) return path
  const usp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue
    if (Array.isArray(value)) {
      for (const v of value) usp.append(key, String(v))
    } else {
      usp.append(key, String(value))
    }
  }
  const q = usp.toString()
  return q ? `${path}?${q}` : path
}

async function parseBody(res: Response): Promise<ApiErrorBody | undefined> {
  try {
    const text = await res.text()
    if (!text) return undefined
    return JSON.parse(text) as ApiErrorBody
  } catch {
    return undefined
  }
}

// ── Single-flight refresh coordinator ────────────────────────────────────
//
// The critical invariant: only ONE /auth/refresh call is in flight at any
// moment. Concurrent 401s wait on the same promise. If two of them each
// spent their refresh token, the backend's reuse-detection branch would
// revoke the whole family — one legitimate reload would sign the reviewer
// out with no error the user could interpret.
//
// The endpoints below opt OUT of the refresh loop to avoid infinite loops:
// - /auth/login: a 401 here means bad credentials, not a stale token
// - /auth/refresh: a 401 here means the refresh itself failed
// - /auth/logout: fire-and-forget; a 401 has no meaningful recovery
const REFRESH_EXEMPT_PATHS = ["/api/v1/auth/login", "/api/v1/auth/refresh", "/api/v1/auth/logout"]

interface RefreshedTokens {
  access_token: string
  expires_in: number
  refresh_token: string | null
  refresh_expires_in: number | null
}

let refreshInFlight: Promise<string | null> | null = null

async function performRefresh(): Promise<string | null> {
  const session = readAuthSession()
  if (!session?.refreshToken) return null

  try {
    const res = await fetch("/api/v1/auth/refresh", {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: session.refreshToken }),
      credentials: "same-origin",
    })
    if (!res.ok) return null
    const wire = (await res.json()) as RefreshedTokens
    const now = Date.now()
    const nextExpiresAt = now + wire.expires_in * 1000
    const nextRefreshExpiresAt =
      wire.refresh_expires_in !== null ? now + wire.refresh_expires_in * 1000 : null
    updateAccessToken(wire.access_token, nextExpiresAt, wire.refresh_token, nextRefreshExpiresAt)
    return wire.access_token
  } catch {
    return null
  }
}

async function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/**
 * Public wrapper for WebSocket / other non-fetch consumers that need the same
 * single-flight refresh semantics as the HTTP client. Returns the new access
 * token, or null when no refresh token is present or the refresh call failed.
 * Uses the same in-flight promise as the 401-recovery path so a burst of
 * simultaneous callers all wait on one /auth/refresh request.
 */
export function refreshAccessTokenIfPossible(): Promise<string | null> {
  return refreshAccessToken()
}

/**
 * How close (ms) to access-token expiry we should preemptively refresh.
 * A WebSocket handshake has no 401 replay path — if the token expires between
 * the read and the server's auth check, the socket closes with 1008 and we
 * reconnect in a loop. Refreshing when < 60s remain gives us a comfortable
 * margin against clock skew.
 */
export const ACCESS_TOKEN_REFRESH_MARGIN_MS = 60_000

// ── Core request ────────────────────────────────────────────────────────

async function fetchOnce(
  url: string,
  method: string,
  finalHeaders: Record<string, string>,
  body: unknown,
  isFormData: boolean,
  signal: AbortSignal | undefined,
): Promise<Response> {
  // Build RequestInit with only the fields that carry a real value.
  // With `exactOptionalPropertyTypes: true`, passing `signal: undefined` or
  // `body: undefined` as literal object keys is a type error even though the
  // fields are optional — we have to omit the key entirely. Same for body.
  const init: RequestInit = {
    method,
    headers: finalHeaders,
    credentials: "same-origin",
  }
  if (body !== undefined && body !== null) {
    init.body = isFormData ? (body as BodyInit) : JSON.stringify(body)
  }
  if (signal !== undefined) {
    init.signal = signal
  }
  return fetch(url, init)
}

async function request<TResponse>(
  method: string,
  path: string,
  { body, params, headers, signal }: BodyOptions<unknown> = {},
): Promise<TResponse> {
  const url = buildUrl(path, params)
  const isFormData = body instanceof FormData

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...headers,
  }

  const token = readAuthToken()
  if (token) finalHeaders["Authorization"] = `Bearer ${token}`

  let res: Response
  try {
    res = await fetchOnce(url, method, finalHeaders, body, isFormData, signal)
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") {
      throw new AbortedError()
    }
    throw new ApiError(0, cause instanceof Error ? cause.message : "Network error")
  }

  // 401 recovery: if we have a refresh token and this endpoint is not itself
  // an auth endpoint, rotate and replay once. Anything else stays untouched.
  const isExempt = REFRESH_EXEMPT_PATHS.some((p) => path === p || path.startsWith(`${p}?`))
  if (res.status === 401 && !isExempt && readAuthSession()?.refreshToken) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      finalHeaders["Authorization"] = `Bearer ${newToken}`
      try {
        res = await fetchOnce(url, method, finalHeaders, body, isFormData, signal)
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === "AbortError") {
          throw new AbortedError()
        }
        throw new ApiError(0, cause instanceof Error ? cause.message : "Network error")
      }
    }
  }

  if (!res.ok) {
    // Still 401 after refresh (or no refresh available) means the session is
    // truly gone. Wipe so RequireAuth bounces to /login on the next render.
    if (res.status === 401) clearAuthSession()

    const errorBody = await parseBody(res)
    const message =
      errorBody?.detail ?? res.statusText ?? `Request failed with status ${String(res.status)}`
    const retryAfterSeconds =
      res.status === 429 ? parseRetryAfter(res.headers.get("Retry-After")) : null
    throw new ApiError(res.status, message, {
      ...(typeof errorBody?.code === "string" ? { code: errorBody.code } : {}),
      ...(errorBody ? { body: errorBody } : {}),
      ...(retryAfterSeconds !== null ? { retryAfterSeconds } : {}),
    })
  }

  if (res.status === 204) return undefined as TResponse

  const text = await res.text()
  if (!text) return undefined as TResponse
  return JSON.parse(text) as TResponse
}

export const http = {
  get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return request<T>("GET", path, opts)
  },
  post<T, B = unknown>(path: string, body?: B, opts?: RequestOptions): Promise<T> {
    return request<T>("POST", path, { ...opts, body })
  },
  put<T, B = unknown>(path: string, body?: B, opts?: RequestOptions): Promise<T> {
    return request<T>("PUT", path, { ...opts, body })
  },
  patch<T, B = unknown>(path: string, body?: B, opts?: RequestOptions): Promise<T> {
    return request<T>("PATCH", path, { ...opts, body })
  },
  delete<T>(path: string, opts?: RequestOptions): Promise<T> {
    return request<T>("DELETE", path, opts)
  },

  /** Fetch raw bytes (PDFs, images) with auth. Returns Uint8Array, not a
   *  blob URL — blob-URL lifetime is a class of bug. */
  async bytes(path: string, opts?: RequestOptions): Promise<Uint8Array> {
    const url = buildUrl(path, opts?.params)
    const token = readAuthToken()
    const headers: Record<string, string> = { ...opts?.headers }
    if (token) headers["Authorization"] = `Bearer ${token}`
    // Same exactOptionalPropertyTypes shape as fetchOnce — omit `signal` when
    // no caller passed one rather than assigning it as `undefined`.
    const init: RequestInit = { method: "GET", headers }
    if (opts?.signal !== undefined) init.signal = opts.signal
    let res: Response
    try {
      res = await fetch(url, init)
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") throw new AbortedError()
      throw new ApiError(0, cause instanceof Error ? cause.message : "Network error")
    }
    if (!res.ok) {
      const errorBody = await parseBody(res)
      throw new ApiError(res.status, errorBody?.detail ?? res.statusText)
    }
    return new Uint8Array(await res.arrayBuffer())
  },
}
